/**
 * SamplerSynth — plays AudioBuffers with per-note pitch shifting
 * (playbackRate) and optional looping. Tracks in-flight sources so the
 * transport can hard-stop them.
 */
export interface SamplerTriggerOptions {
  buffer: AudioBuffer;
  time: number;
  /** 1 = root pitch; 2 = +12 semitones. */
  rate: number;
  loop: boolean;
  /** Velocity scaling, 0..1. */
  gain: number;
  dest: AudioNode;
  /** When to cut the sample (gate end / transport stop). */
  stopAt?: number;
}

export class SamplerSynth {
  private active = new Set<AudioBufferSourceNode>();

  constructor(private readonly ctx: AudioContext) {}

  trigger(opts: SamplerTriggerOptions): void {
    const src = this.ctx.createBufferSource();
    src.buffer = opts.buffer;
    src.playbackRate.value = opts.rate;
    src.loop = opts.loop;
    const g = this.ctx.createGain();
    g.gain.value = opts.gain;
    src.connect(g);
    g.connect(opts.dest);
    src.start(opts.time);
    if (opts.stopAt !== undefined && opts.stopAt > opts.time) {
      src.stop(opts.stopAt);
    }
    this.active.add(src);
    src.onended = () => this.active.delete(src);
  }

  /** Cut off every in-flight sample (transport stop). */
  stopAll(): void {
    for (const src of this.active) {
      try {
        src.stop();
      } catch {
        /* already stopped */
      }
    }
    this.active.clear();
  }
}